import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Axios from '../../api/axios';
import notify from '../../utils/notifyUtil';
import Button from 'devextreme-react/button';
import ReportBootstrapGrid from '../../components/ReportBootstrapGrid';
import ReportLoadingOverlay from '../../components/ReportLoadingOverlay';
import './rapor_whatsapp_toplu.scss';
import '../RAPOR_WHATSAPP_GORUSME/rapor_whatsapp_gorusme.scss';
import AramaGirPanel from '../CARI/AramaGirPanel';
import WhatsAppSagPanel from './WhatsAppSagPanel';

export default function GonderilmisSablonlar({ users, canEdit, onBack, mode = 'sent' }) {
  const aiMode = mode === 'ai';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState({ expected: 0, completed: 0, pending: 0, months: 5 });
  const [buttonOptions, setButtonOptions] = useState([]);
  const [callPanelRow, setCallPanelRow] = useState(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = aiMode ? 'ai_whatsapp_kuyruk' : 'whatsapp_gonderilmis_sablonlar';
      const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/${endpoint}`, {});
      setRows(res.data?.data || []);
      if (aiMode) {
        window.dispatchEvent(new Event('ai-cvp-refresh'));
        setSummary(res.data?.summary || { expected: 0, completed: 0, pending: 0, months: 5 });
        setButtonOptions(res.data?.buttonOptions || []);
      }
    } catch (err) { notify(err?.response?.data?.message || err.message, 'error', 3000); }
    finally { setLoading(false); }
  }, [aiMode]);

  useEffect(() => {
    loadRows();
    if (!aiMode) return undefined;
    const timer = setInterval(loadRows, 30000);
    return () => clearInterval(timer);
  }, [loadRows, aiMode]);

  const selectRow = useCallback((row) => {
    setSelected(row);
    setCallPanelRow(null);
    if (aiMode && row?.CAR_REF) {
      Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/ai_whatsapp_kuyruk_goruldu`, { carRef: row.CAR_REF })
        .then((res) => {
          setRows((current) => current.map((item) => item.CAR_REF === row.CAR_REF
            ? { ...item, AI_GORULDU_TAR: res.data?.seenAt || new Date().toISOString(), AI_GORULDU_DURUM: 'Görüldü' } : item));
        })
        .catch((err) => notify(err?.response?.data?.message || err.message, 'error', 2500));
    }
  }, [aiMode]);

  const followup = useCallback(async (row) => {
    if (!row?.AI_CVP_EVENT_REF) return;
    setSaving(true);
    try {
      const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/whatsapp_cevaplari_takip`, {
        eventRef: row.AI_CVP_EVENT_REF,
      });
      if (res.data?.completed) {
        notify('Arama kaydı AI CVP cevabıyla ilişkilendirildi.', 'success', 2200);
        setCallPanelRow(null);
        await loadRows();
      } else {
        notify('Arama paneli açıldı. Yeni aramayı kaydettiğinizde AI CVP otomatik tamamlanır.', 'warning', 5000);
        setSelected(row);
        setCallPanelRow(row);
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        notify(err.response.data?.message, 'warning', 5000);
        setSelected(row);
        setCallPanelRow(row);
      } else notify(err?.response?.data?.message || err.message, 'error', 2500);
    } finally { setSaving(false); }
  }, [loadRows]);

  const finalizeEmbeddedFollowup = useCallback(async (list) => {
    if (!callPanelRow?.AI_CVP_EVENT_REF) return;
    const hasSavedCall = (Array.isArray(list) ? list : []).some((item) => Number(item?.CD_REF || 0) > 0);
    if (!hasSavedCall) return;
    try {
      const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/whatsapp_cevaplari_takip`, {
        eventRef: callPanelRow.AI_CVP_EVENT_REF,
      });
      if (!res.data?.completed) return;
      notify('Arama kaydı AI CVP cevabıyla ilişkilendirildi.', 'success', 2200);
      setCallPanelRow(null);
      await loadRows();
    } catch (err) {
      if (err?.response?.status !== 409) {
        notify(err?.response?.data?.message || err.message, 'error', 2500);
      }
    }
  }, [callPanelRow, loadRows]);

  const columns = useMemo(() => [
    { dataField: aiMode ? 'AI_CVP_CEVAP_TAR' : 'WTG_GONDERIM_TAR', caption: aiMode ? 'Cevap Tarihi' : 'Gönderim', width: 145, format: 'dd.MM.yyyy HH:mm' },
    ...(aiMode ? [
      { dataField: 'AI_CVP_ETIKET', caption: 'Basılan Etiket', width: 180 },
      ...buttonOptions.map((label, index) => ({
        dataField: `AI_CVP_BUTTON_${index}`,
        caption: label,
        width: 130,
        align: 'center',
        cellRender: (row) => row[`AI_CVP_BUTTON_${index}`] ? '✓' : '',
      })),
      { dataField: 'AI_CVP_DURUM', caption: 'Takip', width: 100 },
      {
        dataField: 'AI_CVP_ARAMA_REF', caption: 'Aradım', width: 80, align: 'center',
        cellRender: (row) => <input type="checkbox" aria-label="Aradım" checked={Boolean(row.AI_CVP_ARAMA_REF)}
          onChange={() => followup(row)} onClick={(e) => e.stopPropagation()} />,
      },
    ] : []),
    ...(aiMode ? [{ dataField: 'AI_HANDOFF', caption: 'AI Durumu', width: 90 }] : [{ dataField: 'WTG_SABLON', caption: 'Gönderilen Şablon', width: 190 }]),
    ...(aiMode ? [{ dataField: 'AI_GORULDU_DURUM', caption: 'İnceleme', width: 100 }] : []),
    { dataField: 'CAR_REF', caption: 'Kart No', width: 85 },
    { dataField: 'CAR_ADSOY', caption: 'Ad Soyad', width: 165 },
    { dataField: aiMode ? 'AI_PHONE' : 'WTG_TELEFON', caption: 'WhatsApp Tel', width: 125 },
    { dataField: 'CAR_TEL1', caption: 'Tel1', width: 115 },
    { dataField: 'CAR_GSM', caption: 'Tel2', width: 115 },
    { dataField: 'CAR_MAIL', caption: 'E-posta', width: 160 },
    { dataField: 'ATANAN_AD', caption: 'Atanan', width: 125 },
    ...(!aiMode ? [
      { dataField: 'DEG_AD', caption: 'Son Değerlendirme', width: 165 },
      { dataField: 'DURUM_AD', caption: 'Son Durum', width: 120 },
      { dataField: 'CD_ARA_TAR', caption: 'Son Arama', width: 140, format: 'dd.MM.yyyy HH:mm' },
      { dataField: 'CD_ILERI_ARA', caption: 'İleri Arama', width: 140, format: 'dd.MM.yyyy HH:mm' },
      { dataField: 'CD_RAN_TAR', caption: 'Randevu', width: 140, format: 'dd.MM.yyyy HH:mm' },
      { dataField: 'CD_ACK', caption: 'Son Açıklama', width: 210 },
    ] : []),
    { dataField: 'CAR_SS_CAMPAIGN', caption: 'Meta Kampanya', width: 160 },
    ...(!aiMode ? [{ dataField: 'GONDEREN_AD', caption: 'Gönderen', width: 120 }] : []),
  ], [aiMode, buttonOptions, followup]);

  return (
    <div className="content-block dx-card responsive-paddings report-page report-loading-overlay-host wa-sent-page">
      <ReportLoadingOverlay visible={loading || saving} />
      <div className="wa-sent-head">
        <div><strong>{aiMode ? 'AI CVP WhatsApp Cevapları' : 'Gönderilmiş Şablonlar'}</strong><span> · aktif takip kuyruğu ({rows.length})</span>
          {aiMode && <span> · Son {summary.months || 5} ay: beklenen {summary.expected || 0}, tamamlanan {summary.completed || 0}, bekleyen {summary.pending || 0}</span>}
        </div>
        <div><Button text="Yenile" onClick={loadRows} />{onBack && <Button text="Toplu gönderime dön" type="default" onClick={onBack} />}</div>
      </div>
      {aiMode && canEdit && summary.byAgent?.length > 0 && <div className="wa-sent-head" style={{ alignItems: 'stretch', gap: 8, overflowX: 'auto' }}>
        {summary.byAgent.map((item) => <div key={item.agentRef || 'unassigned'} className="wa-gorusme-toolbar-chip" style={{ minWidth: 165 }}>
          <strong>{item.agentName}</strong><br />
          Beklenen: {item.expected} · Tamamlanan: {item.completed} · Bekleyen: {item.pending}
        </div>)}
      </div>}
      <div className="wa-sent-layout">
        <div className="wa-sent-grid">
          <ReportBootstrapGrid keyExpr={aiMode ? 'CAR_REF' : 'WTG_REF'} columns={columns} data={rows} totalCount={rows.length}
            loading={loading} clientOperations filterRow defaultPageSize={50} onRowClick={selectRow}
            getRowClassName={(r) => selected?.[aiMode ? 'CAR_REF' : 'WTG_REF'] === r?.[aiMode ? 'CAR_REF' : 'WTG_REF'] ? 'wa-gorusme-row-active' : null}
            emptyText="Aktif gönderim kuyruğunda kayıt yok" />
        </div>
        {callPanelRow ? (
          <div className="wa-gorusme-panel wa-sent-chat-panel">
            <div className="wa-gorusme-panel-head">
              <strong>{callPanelRow.CAR_ADSOY || 'Arama kaydı'}</strong>
              <Button text="WhatsApp'a dön" stylingMode="outlined" onClick={() => setCallPanelRow(null)} />
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <AramaGirPanel
                embedded
                visible
                carRef={callPanelRow.CAR_REF}
                carAdSoy={callPanelRow.CAR_ADSOY}
                onListChange={finalizeEmbeddedFollowup}
              />
            </div>
          </div>
        ) : (
          <WhatsAppSagPanel
            row={selected}
            users={users}
            canEdit={canEdit}
            queueMode={aiMode ? 'ai' : 'gonderim'}
            onRemoved={() => { setSelected(null); loadRows(); }}
            onAssigned={loadRows}
          />
        )}
      </div>
    </div>
  );
}
